import React, { useState, useEffect } from 'react';
import { Line, Bar } from 'react-chartjs-2';
import { 
  Chart as ChartJS, 
  CategoryScale, 
  LinearScale, 
  BarElement, 
  LineElement,
  PointElement,
  Title, 
  Tooltip, 
  Legend,
  TimeScale
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import { format, parseISO, startOfHour, addHours, startOfDay, addDays, startOfWeek, addWeeks } from 'date-fns';

// Регистрация компонентов Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  TimeScale
);

// Цветовая палитра для категорий ошибок
const generateColors = (count) => {
  // Цвета, оптимизированные для людей с цветовой слепотой
  // Используем высокий контраст и избегаем проблемных сочетаний красный-зеленый
  const baseColors = [
    { bg: '#1f77b4', border: '#145A8B' }, // насыщенный синий
    { bg: '#ff7f0e', border: '#CC5500' }, // яркий оранжевый
    { bg: '#2ca02c', border: '#1F7A1F' }, // темно-зеленый
    { bg: '#d62728', border: '#A01E1F' }, // темно-красный
    { bg: '#9467bd', border: '#6B4C87' }, // фиолетовый
    { bg: '#8c564b', border: '#5D3A32' }, // коричневый
    { bg: '#e377c2', border: '#B85D9C' }, // розовый/магента
    { bg: '#7f7f7f', border: '#5A5A5A' }, // серый
    { bg: '#bcbd22', border: '#8B8E18' }, // оливковый
    { bg: '#17becf', border: '#118C9A' }, // циан
    { bg: '#ffd700', border: '#B8980C' }, // золотой
    { bg: '#40e0d0', border: '#2DB3A6' }, // бирюза
    { bg: '#dda0dd', border: '#B37FB3' }, // слива
    { bg: '#98fb98', border: '#6BC26B' }, // светло-зеленый
    { bg: '#f0e68c', border: '#C4B85C' }  // хаки
  ];
  
  // Если категорий больше чем базовых цветов, генерируем дополнительные
  // используя безопасную для цветовой слепоты стратегию
  if (count > baseColors.length) {
    const additionalColors = Array.from({ length: count - baseColors.length }, (_, i) => {
      // Используем различные комбинации яркости и насыщенности
      // для создания различимых цветов
      const baseIndex = i % baseColors.length;
      const brightness = 40 + (i % 4) * 15; // варьируем яркость: 40%, 55%, 70%, 85%
      const saturation = 60 + (Math.floor(i / 4) % 3) * 15; // варьируем насыщенность: 60%, 75%, 90%
      
      // Берем оттенок из базового цвета и модифицируем его
      const baseColor = baseColors[baseIndex];
      const hue = (baseIndex * 25 + i * 15) % 360; // распределяем по спектру
      
      return {
        bg: `hsla(${hue}, ${saturation}%, ${brightness}%, 0.8)`,
        border: `hsla(${hue}, ${Math.min(saturation + 10, 100)}%, ${Math.max(brightness - 15, 20)}%, 1)`
      };
    });
    return [...baseColors, ...additionalColors];
  }
  
  return baseColors.slice(0, count);
};

// Опции временного периода
const TIME_PERIODS = [
  { value: 'hour', label: '1 hour' },
  { value: 'hour4', label: '4 hours' },
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' }
];

// Типы графиков
const CHART_TYPES = [
  { value: 'bar', label: 'Bar Chart' },
  { value: 'line', label: 'Line Chart' }
];

const ErrorsChart = ({ 
  data,
  errorTypes,
  selectedPeriod = 'day',
  onPeriodChange
}) => {
  const [chartType, setChartType] = useState('bar');
  const [chartData, setChartData] = useState(null);
  // Храним сырые данные для использования в тултипах
  const [rawDataByLabel, setRawDataByLabel] = useState({});

  useEffect(() => {
    if (data && errorTypes && errorTypes.length > 0) {
      prepareChartData(selectedPeriod);
    }
  }, [data, errorTypes, selectedPeriod, chartType]);

  const prepareChartData = (period) => {
    if (!data || Object.keys(data).length === 0 || !errorTypes || errorTypes.length === 0) {
      setChartData(null);
      return;
    }
    
    // Сначала собираем все данные с временными метками
    const timeData = Object.entries(data).map(([timestamp, counts]) => ({
      date: parseISO(timestamp),
      ...errorTypes.reduce((acc, type) => ({
        ...acc,
        [type]: counts[type] || 0
      }), {}),
      total: counts.total || 0
    }));

    // Сортируем по дате
    timeData.sort((a, b) => a.date - b.date);

    // Группируем данные в зависимости от выбранного периода
    const groupedData = {};
    // Создаем объект для хранения сырых данных для тултипов
    const rawData = {};
    
    // Определяем функции группировки в зависимости от периода
    const getPeriodStart = (date) => {
      switch (period) {
        case 'hour':
          return startOfHour(date);
        case 'hour4':
          const hour = date.getHours();
          const fourHourBlock = Math.floor(hour / 4) * 4;
          return new Date(date.getFullYear(), date.getMonth(), date.getDate(), fourHourBlock);
        case 'day':
          return startOfDay(date);
        case 'week':
          return startOfWeek(date, { weekStartsOn: 1 }); // Неделя начинается с понедельника
        default:
          return startOfDay(date);
      }
    };
    
    const getNextPeriod = (date) => {
      switch (period) {
        case 'hour':
          return addHours(date, 1);
        case 'hour4':
          return addHours(date, 4);
        case 'day':
          return addDays(date, 1);
        case 'week':
          return addWeeks(date, 1);
        default:
          return addDays(date, 1);
      }
    };
    
    const formatPeriodLabel = (date) => {
      switch (period) {
        case 'hour':
          return format(date, 'dd MMM HH:00');
        case 'hour4':
          return format(date, 'dd MMM HH:00') + ' - ' + format(addHours(date, 3), 'HH:00');
        case 'day':
          return format(date, 'dd MMM yyyy');
        case 'week':
          return format(date, 'dd MMM') + ' - ' + format(addDays(date, 6), 'dd MMM yyyy');
        default:
          return format(date, 'dd MMM yyyy');
      }
    };

    // Инициализируем группы данных
    if (timeData.length > 0) {
      let currentDate = getPeriodStart(timeData[0].date);
      const endDate = getPeriodStart(timeData[timeData.length - 1].date);
      
      // Создаем все временные интервалы, даже пустые
      while (currentDate <= endDate) {
        const periodLabel = formatPeriodLabel(currentDate);
        groupedData[periodLabel] = {
          total: 0,
          ...errorTypes.reduce((acc, type) => ({ ...acc, [type]: 0 }), {})
        };
        // Инициализируем объект для сырых данных тултипа
        rawData[periodLabel] = {
          total: 0,
          ...errorTypes.reduce((acc, type) => ({ ...acc, [type]: 0 }), {}),
          date: currentDate // Сохраняем дату для форматирования в тултипе
        };
        currentDate = getNextPeriod(currentDate);
      }
      
      // Заполняем данными существующие периоды
      timeData.forEach(item => {
        try {
          const periodStart = getPeriodStart(item.date);
          const periodLabel = formatPeriodLabel(periodStart);
          
          if (groupedData[periodLabel]) {
            errorTypes.forEach(errorType => {
              groupedData[periodLabel][errorType] += item[errorType] || 0;
              rawData[periodLabel][errorType] += item[errorType] || 0;
            });
            
            groupedData[periodLabel].total += item.total || 0;
            rawData[periodLabel].total += item.total || 0;
          }
        } catch (error) {
          console.error('Error processing data item:', item, error);
        }
      });
    }

    // Сохраняем сырые данные для использования в тултипах
    setRawDataByLabel(rawData);

    // Формируем цвета для каждого типа ошибок
    const colorPalette = generateColors(errorTypes.length);

    // Преобразуем данные для Chart.js
    const labels = Object.keys(groupedData);
    
    // Создаем датасеты для графиков - без датасета Total
    const datasets = errorTypes.map((errorType, index) => {
      const color = colorPalette[index % colorPalette.length];
      
      return {
        label: errorType,
        data: labels.map(label => groupedData[label][errorType]),
        backgroundColor: color.bg,
        borderColor: color.border,
        hoverBackgroundColor: color.border,
        borderWidth: chartType === 'line' ? 2 : 1,
        pointRadius: chartType === 'line' ? 3 : 0,
        pointHoverRadius: chartType === 'line' ? 5 : 0,
        fill: false,
        tension: 0.1
      };
    });
    
    setChartData({ 
      labels, 
      datasets 
    });
  };

  const handlePeriodChange = (e) => {
    const newPeriod = e.target.value;
    
    // Если предоставлен обработчик внешнего изменения, вызываем его
    if (onPeriodChange) {
      onPeriodChange(newPeriod);
    }
  };
  
  const handleChartTypeChange = (e) => {
    setChartType(e.target.value);
  };

  const formatDate = (date, periodType) => {
    switch (periodType) {
      case 'hour':
        return format(date, 'dd MMM yyyy HH:00');
      case 'hour4':
        return `${format(date, 'dd MMM HH:00')} - ${format(addHours(date, 3), 'HH:00')}`;
      case 'day':
        return format(date, 'dd MMMM yyyy');
      case 'week':
        return `${format(date, 'dd MMM yyyy')} - ${format(addDays(date, 6), 'dd MMM yyyy')}`;
      default:
        return format(date, 'dd MMM yyyy');
    }
  };

  // Настройки для тултипов
  const tooltipSettings = {
    enabled: true,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    titleFont: {
      weight: 'bold',
      size: 12
    },
    bodyFont: {
      size: 12
    },
    padding: 10,
    displayColors: true,
    boxWidth: 10,
    boxHeight: 10,
    usePointStyle: true,
    callbacks: {
      // Формат метки
      label: function(context) {
        const label = context.dataset.label || '';
        const value = context.raw;
        const dataLabel = context.label;
        const rawData = rawDataByLabel[dataLabel];
        
        if (!rawData) return `${label}: ${value.toLocaleString()}`;
        
        // Для категорий ошибок показываем значение и процент от общего
        const percentage = rawData.total > 0 
          ? ((value / rawData.total) * 100).toFixed(1) 
          : 0;
        
        return `${label}: ${value.toLocaleString()} (${percentage}%)`;
      },
      // Форматтер заголовка
      title: function(tooltipItems) {
        const dataLabel = tooltipItems[0].label;
        const rawData = rawDataByLabel[dataLabel];
        
        if (rawData?.date) {
          return formatDate(rawData.date, selectedPeriod);
        }
        
        return dataLabel;
      }
    }
  };

  // Настройки для графиков
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      tooltip: tooltipSettings,
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 15,
          boxWidth: 10
        }
      },
      title: { display: false }
    },
    scales: {
      x: {
        grid: { display: false, offset: chartType === 'bar' },
        ticks: {
          maxRotation: 45,
          minRotation: 45,
          autoSkip: false,
          font: { size: 10 },
          align: 'center',
          color: '#666'
        },
        border: { width: 1 },
        offset: chartType === 'bar',
        stacked: chartType === 'bar'
      },
      y: {
        beginAtZero: true,
        grid: { color: 'rgba(0, 0, 0, 0.05)' },
        ticks: {
          callback: function(value) {
            return value.toLocaleString();
          }
        },
        stacked: chartType === 'bar'
      }
    },
    layout: {
      padding: {
        left: 10,
        right: 10
      }
    },
    animation: { duration: 500 }
  };

  const renderChart = () => {
    if (!chartData) {
      return (
        <div className="h-full w-full flex justify-center items-center">
          <p className="text-gray-500">No data available for chart</p>
        </div>
      );
    }

    if (chartType === 'bar') {
      return <Bar data={chartData} options={chartOptions} />;
    } else {
      return <Line data={chartData} options={chartOptions} />;
    }
  };

  return (
    <div className="w-full bg-white shadow-md rounded-lg p-4 flex flex-col">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-medium text-gray-700">Errors Timeline</h3>
        <div className="flex items-center space-x-4">
          {/* Выбор типа графика */}
          <div className="flex items-center">
            <label htmlFor="chart-type-select" className="mr-2 text-sm text-gray-600">Chart Type:</label>
            <select 
              id="chart-type-select"
              value={chartType} 
              onChange={handleChartTypeChange}
              className="border border-gray-300 rounded-md shadow-sm px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {CHART_TYPES.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>
          
          {/* Выбор временного интервала */}
          <div className="flex items-center">
            <label htmlFor="period-select" className="mr-2 text-sm text-gray-600">Interval:</label>
            <select 
              id="period-select"
              value={selectedPeriod} 
              onChange={handlePeriodChange}
              className="border border-gray-300 rounded-md shadow-sm px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {TIME_PERIODS.map(period => (
                <option key={period.value} value={period.value}>
                  {period.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      
      <div className="h-96 w-full">
        {renderChart()}
      </div>
    </div>
  );
};

export default ErrorsChart;